import React, { useState } from 'react';
import { Alert, Button, Form, InputGroup } from 'react-bootstrap';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../../config/env';
import AuthShell from '../Layout/AuthShell';

const Register: React.FC = () => {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [walletAddress, setWalletAddress] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const isValidUsername = (value: string): boolean => value.trim().length >= 3 && value.trim().length <= 50;

  const isValidEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const isValidPassword = (value: string): boolean => {
    if (value.length < 8) {
      return false;
    }

    return /[a-zA-Z]/.test(value) && /\d/.test(value);
  };

  const isValidWalletAddress = (value: string): boolean => /^0x[a-fA-F0-9]{40}$/.test(value);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    if (!username.trim() || !email.trim() || !password.trim() || !walletAddress.trim()) {
      setError('Please fill in all fields');
      setIsLoading(false);
      return;
    }

    if (!isValidUsername(username)) {
      setError('Username must be between 3 and 50 characters long');
      setIsLoading(false);
      return;
    }

    if (!isValidEmail(email)) {
      setError('Please enter a valid email address');
      setIsLoading(false);
      return;
    }

    if (!isValidPassword(password)) {
      setError('Password must be at least 8 characters long and contain both letters and numbers');
      setIsLoading(false);
      return;
    }

    if (!isValidWalletAddress(walletAddress)) {
      setError('Please enter a valid ERC-20 wallet address (0x followed by 40 hexadecimal characters)');
      setIsLoading(false);
      return;
    }

    try {
      const response = await axios.post(API_ENDPOINTS.REGISTER, {
        username,
        email,
        password,
        usdcWallet: walletAddress,
      }, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.status === 200) {
        navigate('/login', {
          state: {
            message: 'Registration successful! Please log in with your credentials.',
            prefilledUsername: username,
          },
        });
      } else {
        setError('Registration failed. Please try again.');
      }
    } catch (err) {
      if (axios.isAxiosError(err)) {
        if (err.response?.data?.expandedError?.message) {
          setError(err.response.data.expandedError.message);
        } else if (err.response?.status === 400) {
          setError('Invalid registration data. Please check your information.');
        } else if (err.response?.status === 409) {
          setError('An account with this email already exists.');
        } else if (err.response?.status && err.response.status >= 500) {
          setError('Server error. Please try again later.');
        } else {
          setError('Registration failed. Please try again.');
        }
      } else {
        setError('Network error. Please check your connection and try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthShell
      title="Create your account"
      description="Set up your operator workspace and connect the Polygon wallet that receives settled payments."
      footer={(
        <div className="auth-link-row">
          <span className="auth-helper-text">Already onboarded?</span>
          <Link to="/login" className="auth-link">Login here</Link>
        </div>
      )}
    >
      {error ? <Alert variant="danger">{error}</Alert> : null}

      <Form onSubmit={handleSubmit}>
        <Form.Group controlId="username" className="mb-3">
          <Form.Label>Username</Form.Label>
          <Form.Control
            className="auth-control"
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="Choose a username"
            autoComplete="username"
          />
          <Form.Text className="auth-helper-text">
            Must be between 3 and 50 characters.
          </Form.Text>
        </Form.Group>

        <Form.Group controlId="email" className="mb-3">
          <Form.Label>Email Address</Form.Label>
          <Form.Control
            className="auth-control"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email"
            autoComplete="email"
          />
        </Form.Group>

        <Form.Group controlId="password" className="mb-3">
          <Form.Label>Password</Form.Label>
          <InputGroup className="auth-input-group">
            <Form.Control
              className="auth-control"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Minimum 8 characters with letters and numbers"
              autoComplete="new-password"
            />
            <Button
              type="button"
              variant="outline-secondary"
              className="auth-password-toggle"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? 'Hide' : 'Show'}
            </Button>
          </InputGroup>
          <Form.Text className="auth-helper-text">
            Use at least eight characters with both letters and numbers.
          </Form.Text>
        </Form.Group>

        <Form.Group controlId="walletAddress" className="mb-3">
          <Form.Label>Polygon Settlement Wallet</Form.Label>
          <Form.Control
            className="auth-control"
            type="text"
            value={walletAddress}
            onChange={(e) => setWalletAddress(e.target.value)}
            placeholder="0x..."
            autoComplete="off"
          />
          <Form.Text className="auth-helper-text">
            This is the wallet that receives your net settled payouts.
          </Form.Text>
        </Form.Group>

        <div className="d-grid">
          <Button
            variant="primary"
            type="submit"
            className="auth-primary-btn mt-3"
            disabled={isLoading}
          >
            {isLoading ? 'Creating Account...' : 'Register'}
          </Button>
        </div>
      </Form>
    </AuthShell>
  );
};

export default Register;
